import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Wrench, FileSpreadsheet, Phone, Mail, Pencil, ChevronLeft, ChevronRight } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { fmtDate, fmtEUR2 } from "@/lib/format";
import { CARD, Label, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { downloadBlob } from "@/components/hr/hrUtils";
import { AuthImage, BTN, LEVEL_C, MaintProvider, RESULT, RESULT_C, StatusBadge, daysLabel, useMaint } from "@/components/maint/maintUtils";
import { ReportFailureButton } from "@/components/maint/ReportFailure";
import { EquipmentForm } from "@/components/maint/EquipmentForm";
import { InterventionDialog, PlanDialog } from "@/components/maint/InterventionDialogs";
import { ProviderDialog, ContractDialog } from "@/components/maint/ProviderDialogs";
import { FailureDetail } from "@/components/maint/FailureDetail";
import { DocsPanel } from "@/components/maint/DocsQr";
import { FailuresTable, PlansTable } from "@/pages/EquipmentPage";

const TABS = [["dash", "Tableau de bord"], ["eq", "Équipements"], ["pannes", "Pannes"], ["iv", "Interventions"], ["prev", "Maintenance préventive"],
  ["ctrl", "Contrôles"], ["prov", "Prestataires"], ["cal", "Calendrier"], ["docs", "Documents"], ["hist", "Historique"], ["set", "Paramètres"]];

export default function Maintenance() { return <MaintProvider><Shell /></MaintProvider>; }

function Shell() {
  const m = useMaint();
  const [tab, setTab] = useState("dash");
  const [eqs, setEqs] = useState([]);
  const [tick, setTick] = useState(0);
  const refresh = () => setTick((t) => t + 1);
  useEffect(() => { if (m.can("view")) api.get("/maint/equipment?include_archived=true").then((r) => setEqs(r.data)).catch(() => {}); }, [m, tick]);
  const ename = useCallback((id) => eqs.find((e) => e.id === id)?.name || "—", [eqs]);
  if (!m.loaded) return null;
  if (!m.can("view")) return (
    <div className={`${CARD} space-y-4 p-6 text-center`} data-testid="maint-report-only"><h1 className="font-display text-2xl font-black">Parc & Maintenance</h1>
      <p className="text-sm text-gray-500">Un appareil ne fonctionne plus ? Signalez-le en quelques secondes.</p><ReportFailureButton onDone={() => toast.success("Merci, la panne est transmise")} /></div>
  );
  const tabs = TABS.filter(([k]) => k !== "set" || m.can("delete"));
  return (
    <div className="space-y-4 pb-24" data-testid="maint-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">Parc & Maintenance</h1><p className="text-sm text-gray-500">Le carnet de santé technique du cabinet</p></div>
        <ReportFailureButton onDone={refresh} />
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {tabs.map(([k, l]) => <button key={k} data-testid={`maint-tab-${k}`} onClick={() => setTab(k)} className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold ${tab === k ? "bg-[#065F46] text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}>{l}</button>)}
      </div>
      {tab === "dash" && <Dash key={tick} />}
      {tab === "eq" && <EquipList eqs={eqs} onChanged={refresh} />}
      {tab === "pannes" && <Pannes key={tick} ename={ename} />}
      {tab === "iv" && <Interventions ename={ename} />}
      {tab === "prev" && <Plans kind="preventive" ename={ename} />}
      {tab === "ctrl" && <Plans kind="controle" ename={ename} />}
      {tab === "prov" && <Providers ename={ename} />}
      {tab === "cal" && <Calendar eqs={eqs} />}
      {tab === "docs" && <AllDocs ename={ename} />}
      {tab === "hist" && <History ename={ename} />}
      {tab === "set" && <SettingsPanel />}
    </div>
  );
}

const Card = ({ t, items }) => (
  <div className={`${CARD} p-4`}><Label className="mb-2">{t}</Label>
    <div className="space-y-1.5">{items.map(([l, v, c, id]) => <div key={l} className="flex items-center justify-between text-sm"><span className="flex items-center gap-2"><span className={`h-2.5 w-2.5 rounded-full ${c}`} />{l}</span><b data-testid={id}>{v}</b></div>)}</div></div>
);

function Dash() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/maint/dashboard").then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  if (!d) return null;
  const G = "bg-emerald-500", O = "bg-amber-500", R = "bg-rose-500", K = "bg-gray-900";
  return (
    <div className="space-y-4" data-testid="maint-dash">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Card t="Parc" items={[["Équipements", d.parc.total, "bg-gray-300", "md-total"], ["Opérationnels", d.parc.operationnel, G, "md-ok"], ["En panne / dégradés", d.parc.en_panne, R, "md-panne"], ["Immobilisés", d.parc.immobilise, O, "md-immo"], ["À remplacer", d.parc.a_remplacer, O], ["Hors service", d.parc.hors_service, K]]} />
        <Card t="Maintenance" items={[["À réaliser prochainement", d.maintenance.prochaine, O, "md-prochaine"], ["En retard", d.maintenance.retard, R, "md-retard"], ["Contrôles à venir", d.maintenance.controles_a_venir, O], ["Contrôles échus", d.maintenance.controles_echus, R]]} />
        <Card t="Pannes" items={[["Pannes ouvertes", d.pannes.ouvertes, R, "md-ouvertes"], ["Interventions en attente", d.pannes.interventions_attente, O], ["Interventions programmées", d.pannes.interventions_programmees, G], ["Appareils hors service / en panne", d.pannes.hors_service, K]]} />
        <Card t="Garanties / contrats" items={[["Garanties expirant bientôt", d.garanties.garanties, O], ["Contrats expirant bientôt", d.garanties.contrats, O]]} />
      </div>
      <div className={`${CARD} p-4 sm:p-6`}><Label className="mb-2">Alertes</Label>
        {d.alerts.length === 0 ? <p className="text-sm text-emerald-600">Tout est à jour.</p> : d.alerts.map((a, i) => (
          <button key={i} data-testid={`md-alert-${i}`} onClick={() => a.equipment_id && nav(`/maintenance/equipement/${a.equipment_id}`)} className="flex w-full items-center gap-3 border-b border-gray-50 py-2 text-left text-sm">
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${LEVEL_C[a.level]}`}>{a.level === "red" ? "En retard" : "À prévoir"}</span>
            <span className="flex-1">{a.label} <span className="text-gray-400">— {daysLabel(a.days)}</span></span><span className="text-xs text-gray-400">{fmtDate(a.date)}</span>
          </button>
        ))}</div>
    </div>
  );
}

function EquipList({ eqs, onChanged }) {
  const m = useMaint();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [archived, setArchived] = useState(false);
  const [form, setForm] = useState(false);
  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    return eqs.filter((e) => (archived || e.status !== "retire") && (!n || [e.name, e.brand, e.model, e.serial_number, e.manufacturer_ref, e.udi, e.piece, e.zone, e.etage, e.category, e.provider_name].join(" ").toLowerCase().includes(n)));
  }, [eqs, q, archived]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" /><Input data-testid="eq-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, marque, modèle, n° série, pièce, prestataire…" className="rounded-full pl-9" /></div>
        <label className="flex items-center gap-1.5 text-xs text-gray-500"><input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} className="accent-[#065F46]" />Afficher les retirés</label>
        <Button data-testid="eq-export-csv" variant="outline" onClick={() => downloadBlob("/maint/export/parc.csv", "parc-equipements.csv").catch((e) => toast.error(apiErr(e)))} className="rounded-full"><FileSpreadsheet className="mr-1 h-4 w-4" />CSV</Button>
        <Button data-testid="eq-export-xlsx" variant="outline" onClick={() => downloadBlob("/maint/export/parc.xlsx", "parc-equipements.xlsx").catch((e) => toast.error(apiErr(e)))} className="rounded-full"><FileSpreadsheet className="mr-1 h-4 w-4" />Excel</Button>
        {m.can("create") && <Button data-testid="eq-add" onClick={() => setForm(true)} className={BTN}><Plus className="mr-1 h-4 w-4" />Équipement</Button>}
      </div>
      {list.length === 0 ? <EmptyState title="Aucun équipement" subtitle="Ajoutez les appareils du cabinet pour démarrer le carnet d'entretien." /> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((e) => (
            <button key={e.id} data-testid={`eq-card-${e.id}`} onClick={() => nav(`/maintenance/equipement/${e.id}`)} className={`${CARD} flex gap-3 p-3 text-left transition-transform hover:-translate-y-0.5`}>
              <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-gray-100">{e.photo_doc_id ? <AuthImage docId={e.photo_doc_id} className="h-full w-full object-cover" /> : <Wrench className="h-7 w-7 text-gray-300" />}</div>
              <div className="min-w-0 flex-1 space-y-0.5">
                <p className="truncate font-black uppercase">{e.name}</p>
                <p className="truncate text-xs text-gray-500">{[e.brand, e.model].filter(Boolean).join(" ") || e.category}</p>
                <p className="truncate text-xs text-gray-500">{e.piece || "—"}</p>
                <StatusBadge s={e.status} />
                <p className="text-[11px] text-gray-500">Dernier : {fmtDate(e.last_maintenance) || "—"} · Prochain : {fmtDate(e.next_maintenance) || "—"}</p>
                {e.provider_name && <p className="truncate text-[11px] font-semibold text-[#065F46]">{e.provider_name}</p>}
              </div>
            </button>
          ))}
        </div>
      )}
      {form && <EquipmentForm onClose={() => setForm(false)} onSaved={(d) => { setForm(false); onChanged(); nav(`/maintenance/equipement/${d.id}`); }} />}
    </div>
  );
}

function Pannes({ ename }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [showClosed, setShowClosed] = useState(false);
  const load = () => api.get("/maint/failures").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);
  return (
    <div className={`${CARD} p-4 sm:p-6`}>
      <label className="mb-2 flex items-center gap-1.5 text-xs text-gray-500"><input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} className="accent-[#065F46]" />Afficher les pannes clôturées</label>
      <FailuresTable rows={rows.filter((f) => showClosed || f.status !== "cloturee")} ename={ename} onOpen={setOpen} />
      {open && <FailureDetail failure={open} equipmentName={ename(open.equipment_id)} onClose={() => { setOpen(null); load(); }} onChanged={load} />}
    </div>
  );
}

function Interventions({ ename }) {
  const m = useMaint();
  const [rows, setRows] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = () => api.get("/maint/interventions").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);
  return (
    <div className={`${CARD} p-4 sm:p-6`}>
      {m.can("create") && <Button data-testid="iv-add" onClick={() => setDlg({})} className={`mb-3 ${BTN}`}><Plus className="mr-1 h-4 w-4" />Intervention</Button>}
      {rows.length === 0 ? <p className="text-sm text-gray-400">Aucune intervention.</p> : <div className="divide-y divide-gray-50">{rows.map((i) => (
        <button key={i.id} data-testid={`iv-row-${i.id}`} onClick={() => m.can("edit") && setDlg({ initial: i })} className="flex w-full flex-wrap items-center gap-2 py-2.5 text-left text-sm hover:bg-gray-50">
          <span className="w-20 text-xs text-gray-400">{fmtDate(i.date)}</span><span className="min-w-0 flex-1"><b>{ename(i.equipment_id)}</b> — {i.type}{i.technicien ? ` · ${i.technicien}` : ""}</span>
          {i.status === "programmee" ? <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-bold text-sky-700">Programmée</span> : <span className={`text-xs font-semibold ${RESULT_C[i.result]}`}>{RESULT[i.result]}</span>}
          {i.cost ? <span className="text-xs font-bold">{fmtEUR2(i.cost)}</span> : null}
        </button>
      ))}</div>}
      {dlg && <InterventionDialog initial={dlg.initial} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); }} />}
    </div>
  );
}

function Plans({ kind, ename }) {
  const m = useMaint();
  const [rows, setRows] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = () => api.get("/maint/plans").then((r) => setRows(r.data.filter((p) => (kind === "controle") === (p.kind === "controle")))).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps
  const canEdit = kind === "controle" ? m.can("manage_compliance") : m.can("edit");
  return (
    <div className={`${CARD} p-4 sm:p-6`}>
      {kind === "controle" && <p className="mb-3 text-xs text-gray-500">Les obligations réglementaires se paramètrent appareil par appareil (fiche → Modifier → Contrôles & conformité). Rien n'est déduit automatiquement du nom de l'appareil.</p>}
      <PlansTable rows={rows} canEdit={canEdit} ename={ename} onAdd={() => setDlg({})} onEdit={(p) => setDlg({ initial: p })} />
      {dlg && <PlanDialog kind={kind} initial={dlg.initial} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); }} />}
    </div>
  );
}

function Providers({ ename }) {
  const m = useMaint();
  const [rows, setRows] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = () => {
    api.get("/maint/providers").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/maint/contracts").then((r) => setContracts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const pname = (id) => rows.find((p) => p.id === id)?.name || "—";
  const close = (r) => { setDlg(null); if (r) load(); };
  return (
    <div className="space-y-4">
      {m.can("manage_providers") && <div className="flex gap-2"><Button data-testid="prov-add" onClick={() => setDlg({ t: "p" })} className={BTN}><Plus className="mr-1 h-4 w-4" />Prestataire</Button><Button data-testid="contract-add" variant="outline" onClick={() => setDlg({ t: "c" })} className="rounded-full"><Plus className="mr-1 h-4 w-4" />Contrat</Button></div>}
      <div className="grid gap-3 sm:grid-cols-2" data-testid="prov-list">
        {rows.length === 0 && <EmptyState title="Annuaire vide" subtitle="Ajoutez vos sociétés de maintenance et leurs techniciens." />}
        {rows.map((p) => (
          <div key={p.id} className={`${CARD} space-y-1.5 p-4`} data-testid={`prov-card-${p.id}`}>
            <div className="flex items-start justify-between"><div><p className="font-black uppercase">{p.name}</p><p className="text-xs text-gray-500">{p.specialite}</p></div>
              {m.can("manage_providers") && <button onClick={() => setDlg({ t: "p", initial: p })} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"><Pencil className="h-4 w-4" /></button>}</div>
            {p.phone && <a href={`tel:${p.phone.replace(/\s/g, "")}`} className="flex items-center gap-1.5 text-sm"><Phone className="h-3.5 w-3.5" />Standard {p.phone}</a>}
            {p.phone_urgence && <a href={`tel:${p.phone_urgence.replace(/\s/g, "")}`} className="flex items-center gap-1.5 text-sm font-bold text-rose-700"><Phone className="h-3.5 w-3.5" />Urgence {p.phone_urgence}</a>}
            {p.email && <a href={`mailto:${p.email}`} className="flex items-center gap-1.5 text-sm"><Mail className="h-3.5 w-3.5" />{p.email}</a>}
            {(p.contacts || []).map((c) => <p key={c.id} className="text-xs text-gray-600">{c.fonction || "Contact"} : <b>{c.prenom} {c.nom}</b> {c.mobile || c.phone}</p>)}
            {p.equipment.length > 0 && <p className="text-[11px] text-gray-400">Appareils : {p.equipment.map((e) => e.name).join(", ")}</p>}
          </div>
        ))}
      </div>
      <div className={`${CARD} p-4 sm:p-6`} data-testid="contracts-list"><Label className="mb-2">Contrats de maintenance</Label>
        {contracts.length === 0 ? <p className="text-sm text-gray-400">Aucun contrat.</p> : contracts.map((c) => (
          <button key={c.id} onClick={() => m.can("manage_providers") && setDlg({ t: "c", initial: c })} className="flex w-full flex-wrap items-center gap-2 border-b border-gray-50 py-2 text-left text-sm">
            <span className="min-w-0 flex-1"><b>{pname(c.provider_id)}</b> · n° {c.number || "—"} · {(c.equipment_ids || []).map(ename).join(", ")}</span>
            <span className="text-xs">{fmtEUR2(c.cout_annuel)}/an</span><span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs">{fmtDate(c.start)} → {fmtDate(c.end)}</span>
          </button>
        ))}</div>
      {dlg?.t === "p" && <ProviderDialog initial={dlg.initial} onClose={() => close()} onSaved={() => close(true)} />}
      {dlg?.t === "c" && <ContractDialog initial={dlg.initial} onClose={() => close()} onSaved={() => close(true)} />}
    </div>
  );
}

const KIND = { preventive: ["Maintenance", "bg-emerald-500"], controle: ["Contrôle", "bg-violet-500"], programmee: ["Intervention", "bg-sky-500"], garantie: ["Garantie", "bg-amber-500"], contrat: ["Contrat", "bg-rose-500"] };

function Calendar({ eqs }) {
  const [cur, setCur] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [ev, setEv] = useState([]);
  const [flt, setFlt] = useState({ kind: "", equipment: "", category: "", site: "" });
  const p2 = (n) => String(n).padStart(2, "0");
  const n = new Date(cur.y, cur.m + 1, 0).getDate();
  useEffect(() => { api.get(`/maint/calendar?start=${cur.y}-${p2(cur.m + 1)}-01&end=${cur.y}-${p2(cur.m + 1)}-${n}`).then((r) => setEv(r.data)).catch(() => {}); }, [cur, n]);
  const shown = ev.filter((e) => (!flt.kind || e.kind === flt.kind) && (!flt.equipment || e.equipment_id === flt.equipment) && (!flt.category || e.category === flt.category) && (!flt.site || e.site_id === flt.site));
  const first = (new Date(cur.y, cur.m, 1).getDay() + 6) % 7;
  const cells = [...Array(first).fill(null), ...Array.from({ length: n }, (_, i) => i + 1)];
  const cats = [...new Set(eqs.map((e) => e.category).filter(Boolean))];
  const sel = (k, opts, ph) => <select data-testid={`cal-filter-${k}`} value={flt[k]} onChange={(e) => setFlt({ ...flt, [k]: e.target.value })} className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs"><option value="">{ph}</option>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>;
  const shift = (d) => setCur(({ y, m }) => { const t = new Date(y, m + d, 1); return { y: t.getFullYear(), m: t.getMonth() }; });
  return (
    <div className={`${CARD} p-4 sm:p-6`} data-testid="maint-calendar">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button onClick={() => shift(-1)} className="rounded-full p-1.5 hover:bg-gray-100"><ChevronLeft className="h-4 w-4" /></button>
        <span className="w-36 text-center font-semibold capitalize">{new Date(cur.y, cur.m, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</span>
        <button onClick={() => shift(1)} className="rounded-full p-1.5 hover:bg-gray-100"><ChevronRight className="h-4 w-4" /></button>
        {sel("kind", Object.entries(KIND).map(([k, v]) => [k, v[0]]), "Tous types")}{sel("equipment", eqs.map((e) => [e.id, e.name]), "Tous équipements")}{sel("category", cats.map((c) => [c, c]), "Toutes catégories")}
      </div>
      <div className="grid grid-cols-7 gap-1 text-xs">
        {["L", "M", "M", "J", "V", "S", "D"].map((d, i) => <div key={i} className="text-center font-semibold text-gray-400">{d}</div>)}
        {cells.map((d, i) => { const iso = d && `${cur.y}-${p2(cur.m + 1)}-${p2(d)}`; const items = d ? shown.filter((e) => e.date === iso) : []; return (
          <div key={i} className={`min-h-[64px] rounded-lg p-1 ${d ? "bg-gray-50" : ""}`}>{d && <p className="text-[11px] font-semibold text-gray-500">{d}</p>}
            {items.map((e, j) => <p key={j} title={e.label} className="mt-0.5 flex items-center gap-1 truncate text-[10px]"><span className={`h-1.5 w-1.5 shrink-0 rounded-full ${KIND[e.kind]?.[1]}`} />{e.label}</p>)}</div>
        ); })}
      </div>
    </div>
  );
}

function AllDocs({ ename }) {
  const [docs, setDocs] = useState([]);
  const load = () => api.get("/maint/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return <div className={`${CARD} p-4 sm:p-6`}><p className="mb-2 text-xs text-gray-500">Pour ajouter un document, ouvrez la fiche de l'appareil → Documents.</p><DocsPanel docs={docs} onChanged={load} showEquipment={ename} /></div>;
}

function History({ ename }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/maint/audit").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className={`${CARD} p-4 sm:p-6`} data-testid="maint-audit">{rows.length === 0 ? <p className="text-sm text-gray-400">Aucune action enregistrée.</p> : rows.map((a) => (
      <p key={a.id} className="border-b border-gray-50 py-1.5 text-sm"><span className="text-xs text-gray-400">{fmtDate(a.at)} {a.at.slice(11, 16)} UTC</span> · <b>{a.user_name}</b> — {a.label}{a.equipment_id && !a.label.includes(ename(a.equipment_id)) ? ` (${ename(a.equipment_id)})` : ""}</p>
    ))}</div>
  );
}

function SettingsPanel() {
  const m = useMaint();
  const [f, setF] = useState({ categories: (m.categories || []).join("\n"), intervention_types: (m.intervention_types || []).join("\n"), alert_days: (m.alert_days || []).join(", ") });
  const save = async () => {
    try {
      await api.put("/maint/settings", { categories: f.categories.split("\n"), intervention_types: f.intervention_types.split("\n"), alert_days: f.alert_days.split(",").map((x) => parseInt(x, 10)).filter((x) => !Number.isNaN(x)) });
      toast.success("Paramètres enregistrés"); m.reload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className={`${CARD} grid gap-4 p-4 sm:grid-cols-2 sm:p-6`} data-testid="maint-settings">
      <div><Label>Catégories d'équipements (une par ligne)</Label><textarea data-testid="set-categories" value={f.categories} onChange={(e) => setF({ ...f, categories: e.target.value })} rows={12} className="mt-1 w-full rounded-xl border border-gray-200 p-2 text-sm" /></div>
      <div><Label>Types d'interventions (une par ligne)</Label><textarea data-testid="set-types" value={f.intervention_types} onChange={(e) => setF({ ...f, intervention_types: e.target.value })} rows={12} className="mt-1 w-full rounded-xl border border-gray-200 p-2 text-sm" /></div>
      <div><Label>Alertes : jours avant échéance (séparés par des virgules, 0 = le jour même)</Label><Input data-testid="set-alert-days" value={f.alert_days} onChange={(e) => setF({ ...f, alert_days: e.target.value })} className="mt-1 rounded-xl" />
        <p className="mt-1 text-xs text-gray-400">Les échéances dépassées sont toujours signalées « en retard ». Les alertes sont prêtes pour un futur envoi par email ou notification.</p></div>
      <div className="flex items-end"><Button data-testid="set-save" onClick={save} className={`w-full ${BTN}`}>Enregistrer</Button></div>
    </div>
  );
}
